import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { Controller, useForm } from 'react-hook-form';
import { Button, FormGroup, color, spacing, typography } from '@uniquegood/realworld-studio-design';
import { coreApi } from '@/api';
import { ClickEventName, InputEventName, track } from '@/track';
import DagooMagic from '../../../../public/assets/icons/dagoo-magic.svg';

interface CreateChannelFormProps {
  onNext: (data: { name: string; description: string }) => void;
}

export default function CreateChannelForm({ onNext }: CreateChannelFormProps) {
  const { handleSubmit, watch, formState, control, setError, clearErrors } = useForm<{
    name: string;
    description: string;
  }>();

  const [watchName] = watch(['name']);
  const timeoutId = useRef<number | null>(null);

  useEffect(() => {
    clearErrors('name');

    if (!watchName?.trim().length) {
      setError('name', { type: 'value' });
      return;
    }

    if (watchName.includes('리얼월드')) {
      setError('name', { type: 'value', message: '리얼월드와 유사한 이름은 사용할 수 없습니다' });
      return;
    }

    if (timeoutId.current) {
      window.clearTimeout(timeoutId.current);
    }

    timeoutId.current = window.setTimeout(async () => {
      const { data: isAvailableName } = await coreApi.get<boolean | CoreAPIError>(
        `/api/apps/checkName`,
        {
          params: { name: watchName }
        }
      );

      if (isAvailableName === false) {
        setError('name', { type: 'value', message: '이미 사용 중인 이름입니다' });
      }
    }, 250);
  }, [watchName, setError, clearErrors]);

  const disabled = formState.errors.name?.type === 'value';

  return (
    <div css={container}>
      <div css={headerContainer}>
        <img src={DagooMagic} alt="Dagoo Magic" css={imageStyle} />
        <div css={titleStyle}>나만의 채널을 만들어요!</div>
        <div css={subtitleStyle}>설정은 언제든 바꿀 수 있어요</div>
      </div>

      <form onSubmit={handleSubmit(onNext)} css={formStyle}>
        <div css={formGroupsWrapper}>
          <Controller
            control={control}
            name="name"
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <FormGroup label="채널 이름" requiredIndicator errorText={fieldState.error?.message}>
                <div css={inputWrapper}>
                  <input
                    {...field}
                    type="text"
                    id="channelName"
                    css={fieldState.error?.message ? inputErrorStyle : inputStyle}
                    onChange={(e) => {
                      // 한국어 IME 조합 문제 대응: 직접 길이 제한
                      if (e.target.value.length > 30) {
                        e.target.value = e.target.value.slice(0, 30);
                      }
                      field.onChange(e);
                      track.onInputThrottled({
                        inputEventName: InputEventName.input_newchannelpage_channelname
                      });
                    }}
                    placeholder="예) 윤익월드"
                    maxLength={30}
                  />
                  <div css={inputCounterStyle(field.value?.length > 0)}>
                    {field.value?.length ? field.value.length : 0}/30
                  </div>
                </div>
              </FormGroup>
            )}
          />
          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <FormGroup label="채널 한 줄 소개">
                <div css={inputWrapper}>
                  <input
                    {...field}
                    type="text"
                    id="channelDescription"
                    css={inputStyle}
                    onChange={(e) => {
                      // 한국어 IME 조합 문제 대응: 직접 길이 제한
                      if (e.target.value.length > 80) {
                        e.target.value = e.target.value.slice(0, 80);
                      }
                      field.onChange(e);
                      track.onInputThrottled({
                        inputEventName: InputEventName.input_newchannelpage_channelsummery
                      });
                    }}
                    placeholder="예) 내가 만든 게임으로 세상을 윤익하게!"
                    maxLength={80}
                  />
                  <div css={inputCounterStyle(field.value?.length > 0)}>
                    {field.value?.length ? field.value.length : 0}/80
                  </div>
                </div>
              </FormGroup>
            )}
          />
        </div>
        <Button
          type="primary"
          size="large"
          htmlType="submit"
          disabled={disabled}
          cssStyle={buttonStyle}
          onClick={() => {
            track.onClick({
              clickEventName: ClickEventName.click_newchannelpage_button_start
            });
          }}
        >
          다음
        </Button>
      </form>
    </div>
  );
}

const container = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: ${spacing.common.large};
  background-color: ${color.surface_default_default};
`;

const headerContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.common.medium};
  padding: ${spacing.common.small} 0;
`;

const imageStyle = css`
  width: 160px;
  height: 140px;
`;

const titleStyle = css`
  color: ${color.text_default};
  text-align: center;
  font-family: ${typography.family.primary};
  font-size: 28px;
  font-weight: ${typography.weight.bold};
  line-height: 32px;
`;

const subtitleStyle = css`
  color: ${color.text_subdued};
  text-align: center;
  font-family: ${typography.family.primary};
  font-size: ${typography.size.s2};
  font-weight: ${typography.weight.regular};
  line-height: 20px;
`;

const formStyle = css`
  display: flex;
  flex-direction: column;
  max-width: 320px;
  width: 100%;
  gap: 40px;
  margin-top: 40px;
`;

const formGroupsWrapper = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
`;

const buttonStyle = css`
  width: 100%;
`;

const inputWrapper = css`
  position: relative;
`;

const inputStyle = css`
  width: 100%;

  color: #0e1017;
  font-family: Pretendard sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;

  display: flex;
  height: 40px;
  padding: 0 55px 0 12px;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;

  border-radius: 8px;
  border: 1px solid #e9ebf1;
  background-color: ${color.surface_default_subdued};

  outline: none;

  &:focus {
    outline: 1px solid #7800ff;
  }

  &::placeholder {
    color: #b2b6c2;
  }
`;

const inputErrorStyle = css`
  width: 100%;

  color: #0e1017;
  font-family: Pretendard sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;

  display: flex;
  height: 40px;
  padding: 0 55px 0 12px;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;

  border-radius: 8px;
  border: 1px solid #e9ebf1;

  outline: 1px solid #ee416b;

  &::placeholder {
    color: #b2b6c2;
  }
`;

const inputCounterStyle = (isValue: boolean) => css`
  color: ${isValue ? '#0e1017' : ' #B2B6C2'};

  font-family: Pretendard sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;

  position: absolute;
  top: 0;
  right: 0;
  padding: 9px;
`;
